import styled, { css, keyframes } from "styled-components";

import type { TourPlacement } from "./steps";

const ACCENT = "var(--orange-yellow, #f6730e)";

const spotlightPulse = keyframes`
  0% {
    outline-color: var(--orange-yellow);
    outline-offset: 4px;
  }

  50% {
    outline-color: #ffffff00;
    outline-offset: 8px;
  }

  100% {
    outline-color: var(--orange-yellow);
    outline-offset: 4px;
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: transparent;
`;

export const Spotlight = styled.div`
  position: fixed;
  z-index: 10003;
  border-radius: 2px;
  pointer-events: none;

  box-shadow: 0 0 0 9999px rgba(17, 17, 17, 0.3);

  @media (prefers-reduced-motion: no-preference) {
    transition:
      top 0.25s ease,
      left 0.25s ease,
      width 0.25s ease,
      height 0.25s ease;
  }
`;

export const SpotlightOutline = styled.div<{ focus: boolean }>`
  position: fixed;
  z-index: 10006;
  border-radius: 2px;
  pointer-events: none;

  outline: ${({ focus }) => (focus ? "3px solid var(--orange-yellow)" : "none")};
  outline-offset: 4px;

  animation: ${({ focus }) => (focus ? css`${spotlightPulse} 1.2s infinite` : "none")};

  @media (prefers-reduced-motion: no-preference) {
    transition:
      top 0.25s ease,
      left 0.25s ease,
      width 0.25s ease,
      height 0.25s ease;
  }
`;

const tailPositionByPlacement: Record<TourPlacement, ReturnType<typeof css> | ""> = {
  top: css`
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
  `,
  bottom: css`
    top: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
  `,
  left: css`
    right: -6px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
  `,
  right: css`
    left: -6px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
  `,
  center: "",
  "right-top": css`
    left: -6px;
    top: 20%;
    transform: translateY(-50%) rotate(45deg);
  `,
};

export const TooltipBox = styled.div<{ $placement: TourPlacement; $mobile?: boolean }>`
  position: fixed;
  z-index: 10005;
  width: ${({ $mobile }) => ($mobile ? "100%" : "min(340px, calc(100vw - 32px))")};
  background: #ffffff;
  color: #1f2430;
  border-radius: ${({ $mobile }) => ($mobile ? "20px 20px 0 0" : "16px")};
  padding: ${({ $mobile }) => ($mobile ? "18px 20px calc(18px + env(safe-area-inset-bottom))" : "20px")};
  box-sizing: border-box;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.2), 0 12px 32px rgba(0, 0, 0, 0.25);
  color: var(--text-gray);

  @media (prefers-reduced-motion: no-preference) {
    transition: top 0.25s ease, left 0.25s ease, bottom 0.25s ease;
  }

  &::after {
    content: "";
    position: absolute;
    width: 14px;
    height: 14px;
    background: inherit;
    display: ${({ $placement, $mobile }) => ($mobile || $placement === "center" ? "none" : "block")};
    ${({ $placement }) => tailPositionByPlacement[$placement]}
  }
  h1{
    font-size: 1rem;
    font-weight: 600;
    color: var(--black);
  }

`;

export const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 2px;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  color: #8a8f9c;
  cursor: pointer;

  &:hover {
    color: #1f2430;
  }

  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }
`;

export const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 10px;
  padding-right: 24px;

  padding-bottom: 16px;
  margin-bottom: 16px;

`;


export const TextContent = styled.div`
  flex: 1;
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 1.5;


`;



export const IconInline = styled.img`
  width: 20px;
  height: 20px;
  vertical-align: middle;

  filter: grayscale(100%) brightness(0);
`;

export const GestureButton = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${ACCENT};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    filter: brightness(1.1);
  }

  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }
`;

export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const NavButtons = styled.span`
  display: flex;
  gap: 8px;
`;

export const ProgressContainer = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: 16px;
`;

export const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-bottom: 4px;

  font-size: 14px;
  line-height: 1;
  color: #8a8f9c;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 5px;
  background: #dfe3e8;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $progress: number }>`
  width: ${({ $progress }) => `${$progress}%`};
  height: 100%;
  background: var(--primary-blue);

  transition: width 0.25s ease;
`;

export const NavButton = styled.button<{ $mobile?: boolean }>`
  border: 1px solid var(--primary-blue);
  padding: ${({ $mobile }) => ($mobile ? "12px 18px" : "10px 10px")};
  background: var(--white);
  color: var(--primary-blue);
  font-size: 16px;
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:not(:disabled):hover {
    background: #f2f4f8;
  }

  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }

  &.finish{
    background-color: var(--primary-blue);
    color: var(--white);
  }

  &.finish:hover{
    background-color: var(--very-dark-blue);
  }
`;

export const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const Dot = styled.span<{ $active: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? "var(--dark-blue)" : "#dfe3ea")};

  @media (prefers-reduced-motion: no-preference) {
    transition: background 0.2s ease;
  }
`;
